import React, { useCallback, useEffect, useState } from "react";
import { Eye, Users, Loader2, RefreshCw, Download, Smartphone, Monitor, MailCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const day = (iso) => new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

const Stat = ({ label, value, hint, testId }) => (
  <Card className="p-5 border-slate-200" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
    <p className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </Card>
);

function Bars({ series }) {
  const max = Math.max(1, ...series.map((s) => s.views));
  return (
    <div className="flex items-end gap-[3px] h-28" data-testid="analytics-chart">
      {series.map((s) => (
        <div key={s.date} className="flex-1 group relative" title={`${day(s.date)}: ${s.views} views, ${s.visitors} visitors`}>
          <div className="w-full bg-teal-500/80 hover:bg-teal-600 rounded-t transition-colors"
               style={{ height: `${Math.max(2, (s.views / max) * 100)}%` }} />
        </div>
      ))}
    </div>
  );
}

function List({ title, rows, labelKey, icon: Icon, testId }) {
  if (rows.length === 0) return null;
  const max = Math.max(1, ...rows.map((r) => r.views));
  return (
    <Card className="p-5 border-slate-200" data-testid={testId}>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5 mb-4">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </p>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r[labelKey]} className="text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-slate-700 truncate">{r[labelKey] || "/"}</span>
              <span className="text-slate-500 shrink-0">{r.views}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
              <div className="h-full bg-teal-500 rounded-full" style={{ width: `${(r.views / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function AudienceTable() {
  const [data, setData] = useState(null);
  const [optedOnly, setOptedOnly] = useState(false);

  useEffect(() => { api.get("/admin/audience").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-teal-600" />;

  const people = optedOnly ? data.people.filter((p) => p.marketing_opt_in) : data.people;

  const csv = () => {
    const head = ["Email", "Name", "Has account", "Marketing opt-in", "Sources", "Orders", "Total spent AUD", "Items", "First seen", "Last seen"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = people.map((p) => [p.email, p.name, p.has_account ? "Yes" : "No", p.marketing_opt_in ? "Yes" : "No",
      p.sources.join(" / "), p.orders, p.spent, p.items.join(" | "), fmtDate(p.first_seen), fmtDate(p.last_seen)].map(esc).join(","));
    const blob = new Blob([[head.map(esc).join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `teaching-pathways-audience-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4" data-testid="audience-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="People" value={data.count} hint="Unique email addresses" testId="audience-total" />
        <Stat label="Customers" value={data.customers} hint="Have bought something" testId="audience-customers" />
        <Stat label="Google accounts" value={data.accounts} hint="Signed in to the site" testId="audience-accounts" />
        <Stat label="Opted in to email" value={data.opted_in} hint="Ticked the marketing box" testId="audience-optedin" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant={optedOnly ? "default" : "outline"} className="rounded-full"
                onClick={() => setOptedOnly((v) => !v)} data-testid="audience-filter-optin">
          <MailCheck className="w-4 h-4" /> {optedOnly ? "Showing opted-in only" : "Show opted-in only"}
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={csv} data-testid="audience-csv">
          <Download className="w-4 h-4" /> Download CSV ({people.length})
        </Button>
      </div>
      {people.length === 0 ? <p className="text-sm text-slate-500" data-testid="audience-empty">Nobody here yet.</p> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm" data-testid="audience-table">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="p-3">Person</th><th className="p-3">How we know them</th>
                <th className="p-3">Bought</th><th className="p-3 text-right">Spent</th><th className="p-3">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.email} className="border-t border-slate-100 align-top" data-testid={`audience-row-${p.email}`}>
                  <td className="p-3">
                    <span className="block font-medium text-slate-900">{p.name || "—"}</span>
                    <a href={`mailto:${p.email}`} className="text-teal-700 hover:underline">{p.email}</a>
                  </td>
                  <td className="p-3">
                    <span className="flex flex-wrap gap-1">
                      {p.sources.map((s) => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}
                      {p.marketing_opt_in && <Badge className="text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Opted in</Badge>}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600">{p.items.length ? p.items.join(", ") : "—"}</td>
                  <td className="p-3 text-right font-medium">{p.spent ? `$${p.spent}` : "—"}</td>
                  <td className="p-3 text-slate-500 whitespace-nowrap">{fmtDate(p.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-500">
        Australian spam law needs consent before marketing email. Use the “opted-in only” list for newsletters;
        everyone else is fine to contact about their own order.
      </p>
    </div>
  );
}

export default function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async (d) => {
    setBusy(true);
    try { setData((await api.get(`/admin/analytics?days=${d}`)).data); } catch { setData(null); }
    setBusy(false);
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  if (busy && !data) return <Loader2 className="w-5 h-5 animate-spin text-teal-600" data-testid="analytics-loading" />;
  if (!data) return <p className="text-sm text-slate-500">Could not load analytics.</p>;

  const noData = data.totals.views === 0;

  return (
    <div className="space-y-8" data-testid="analytics-panel">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {[7, 30, 90].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} className="rounded-full"
                    onClick={() => setDays(d)} data-testid={`analytics-range-${d}`}>Last {d} days</Button>
          ))}
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => load(days)} data-testid="analytics-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
        {noData ? (
          <Card className="p-6 border-slate-200" data-testid="analytics-empty">
            <p className="text-slate-700 font-medium">No visits recorded yet.</p>
            <p className="text-sm text-slate-500 mt-1">
              Tracking starts the moment this is live on teachingpathways.com.au. Visits from the preview site and
              known bots are excluded, so the numbers reflect real people.
            </p>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Visitors" value={data.totals.visitors} hint={`Last ${days} days`} testId="analytics-visitors" />
              <Stat label="Page views" value={data.totals.views} hint={`Last ${days} days`} testId="analytics-views" />
              <Stat label="Visitors (7 days)" value={data.totals.visitors_7_days} testId="analytics-visitors-7" />
              <Stat label="Views (7 days)" value={data.totals.views_7_days} testId="analytics-views-7" />
            </div>
            <Card className="p-5 border-slate-200">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">Page views per day</p>
              <Bars series={data.series} />
              <div className="flex justify-between text-xs text-slate-400 mt-2">
                <span>{day(data.series[0].date)}</span><span>{day(data.series[data.series.length - 1].date)}</span>
              </div>
            </Card>
            <div className="grid md:grid-cols-2 gap-4">
              <List title="Most visited pages" rows={data.top_pages} labelKey="path" icon={Eye} testId="analytics-pages" />
              <List title="Where visitors came from" rows={data.sources} labelKey="source" icon={Users} testId="analytics-sources" />
            </div>
            <div className="flex gap-6 text-sm text-slate-600">
              <span className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-teal-600" /> Mobile {data.devices.mobile}</span>
              <span className="flex items-center gap-2"><Monitor className="w-4 h-4 text-teal-600" /> Desktop {data.devices.desktop}</span>
            </div>
          </>
        )}
      </div>
      <AudienceTable />
    </div>
  );
}
